import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'lodash/fp';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState, Option } from '@mews-commander/types';
import { PricingMode } from '@mews-commander/data-access';
import { EnterpriseType } from '@mews-commander/legacy-data-structures';
import {
    AllCurrencySelectField,
    commanderForm,
    email,
    EnumifySelectField,
    EnumSelectField,
    formValuesSelector,
    InputField,
    LanguageSelectField,
    presence,
    SelectField,
    url,
    validation,
} from '@mews-commander/legacy-form';
import { getCountryOptions } from '@mews-commander/legacy-entities/src/ducks/countries';
import { getCultureOptions } from '@mews-commander/legacy-entities/src/ducks/cultures';
import { FieldsContainer } from '@mews-commander/ui';
import { AddressFormFields } from '../../../../components/form';
import { isNewChain } from '../../utils';
import { addSettings, autocompleteAddress } from '../../actions/settings';
import { changeStep } from '../../actions/steps';
import EnterpriseBuilderScreenStep from '../../EnterpriseBuilderScreenStep';
import LegalEnvironmentSelectField from './LegalEnvironmentSelectField';
import TimeZoneSelectField from './TimeZoneSelectField';
import ChainSelectField from './ChainSelectField';
import SubscriptionSelectField from './SubscriptionSelectField';
import CitySearchField from './CitySearchField';

const LEGAL_ENVIRONMENTS_WITH_REQUIRED_COMPANY_NAME = ['FR'];

const isCompanyNameRequired = (legalEnvironment: string) =>
    LEGAL_ENVIRONMENTS_WITH_REQUIRED_COMPANY_NAME.includes(legalEnvironment);

const getConstraints = ({ legalEnvironment }: { legalEnvironment: string }) => ({
    name: {
        presence,
    },
    uniqueName: {
        presence,
    },
    type: {
        presence,
    },
    subscription: {
        presence,
    },
    cityId: {
        presence,
    },
    chain: {
        presence,
    },
    newChainName: {
        presence,
    },
    telephone: {
        presence,
    },
    email: {
        presence,
        email,
    },
    legalEmail: {
        presence,
        email,
    },
    websiteUrl: {
        presence,
        url,
    },
    countryCode: {
        presence,
    },
    language: {
        presence,
    },
    culture: {
        presence,
    },
    currency: {
        presence,
    },
    legalEnvironment: {
        presence,
    },
    timeZone: {
        presence,
    },
    companyName: isCompanyNameRequired(legalEnvironment) ? { presence } : {},
});

interface FormValues {
    chain: string;
    legalEnvironment: string;
}

interface StateProps {
    legalEnvironment: string;
    hasExistingChainSelected: boolean;
    countryOptions: Option[];
    cultureOptions: Option[];
}

interface OwnProps {
    form: string;
}

const enterpriseFormSelector = formValuesSelector<FormValues>;

const EnterpriseForm = ({
    countryOptions,
    cultureOptions,
    onAutocomplete,
    hasExistingChainSelected,
    form,
    scope,
    initialValues: { cityId },
    legalEnvironment,
}: $TSFixMe) => (
    <FieldsContainer>
        <InputField name="name" label={<LocalizedText textKey={TextKey.Name} />} required />
        <InputField name="uniqueName" label={<LocalizedText textKey={TextKey.UniqueName} />} required />
        <EnumifySelectField
            name="type"
            enumClass={EnterpriseType}
            label={<LocalizedText textKey={TextKey.Type} />}
            required
        />
        <SubscriptionSelectField />
        <CitySearchField form={form} preselectedResultId={cityId} required />
        <ChainSelectField />
        {!hasExistingChainSelected ? (
            <>
                <InputField
                    name="newChainName"
                    label={<LocalizedText textKey={TextKey.ChainName} />}
                    helper={<LocalizedText textKey={TextKey.NewChainNameHint} />}
                    required
                />
                <InputField name="legalEmail" label={<LocalizedText textKey={TextKey.LegalEmail} />} required />
            </>
        ) : null}
        <InputField name="telephone" label={<LocalizedText textKey={TextKey.Telephone} />} required />
        <InputField name="email" label={<LocalizedText textKey={TextKey.Email} />} required />
        <InputField
            name="websiteUrl"
            label={<LocalizedText textKey={TextKey.WebsiteUrl} />}
            helper={<LocalizedText textKey={TextKey.UrlFormatHint} />}
            required
        />
        <AddressFormFields
            countryOptions={countryOptions}
            onAutocomplete={onAutocomplete}
            scope={scope}
            requiredFields={{
                country: true,
                addressLine1: false,
                addressLine2: false,
                postalCode: false,
                city: false,
            }}
        />
        <LanguageSelectField required />
        <SelectField
            name="culture"
            label={<LocalizedText textKey={TextKey.DefaultCulture} />}
            required
            options={cultureOptions}
        />
        <AllCurrencySelectField name="currency" label={<LocalizedText textKey={TextKey.DefaultCurrency} />} required />
        <LegalEnvironmentSelectField required />
        <InputField
            name="companyName"
            label={<LocalizedText textKey={TextKey.CompanyName} />}
            required={isCompanyNameRequired(legalEnvironment)}
        />
        <EnumSelectField
            name="pricingMode"
            label={<LocalizedText textKey={TextKey.PricingMode} />}
            enumClass={PricingMode}
            enumPrefix="PricingMode"
        />
        <TimeZoneSelectField required />
    </FieldsContainer>
);

export default compose(
    connect<StateProps, {}, OwnProps, ApplicationState>(
        createStructuredSelector({
            countryOptions: getCountryOptions,
            cultureOptions: getCultureOptions,
            hasExistingChainSelected: (state: ApplicationState, { form }: { form: string }) => {
                const formValues = enterpriseFormSelector(state, { form });
                const id = formValues?.chain;

                return !isNewChain(id);
            },
            legalEnvironment: (state: ApplicationState, { form }: { form: string }) => {
                const formValues = enterpriseFormSelector(state, { form });
                return formValues?.legalEnvironment;
            },
        }),
        (dispatch, { form }: { form: string }) => ({
            // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe) => void' is not assignable to parameter of type 'Action<any>'.
            onAutocomplete: (place: $TSFixMe) => dispatch(autocompleteAddress(form, place)),
            onSubmit: (values: $TSFixMe) => {
                // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe) => void' is not assignable to parameter of type 'Action<any>'.
                dispatch(addSettings(values));
                dispatch(changeStep(EnterpriseBuilderScreenStep.ResourceCategories));
            },
        })
    ),
    commanderForm({
        submitLabel: <LocalizedText textKey={TextKey.Save} />,
        validate: validation(getConstraints),
        submitButtonProps: { variant: 'primary' },
    })
)(EnterpriseForm);
